import { z } from 'zod';

/**
 * Phase 2 polish — Knowledge browse endpoint.
 *
 * Companion to the already-shipped `knowledge-events.dto.ts` (ingest
 * contract) and the MCP `semantic_search` resource.  This DTO covers
 * the *admin browse* surface: list indexed chunks for a team with
 * pagination + text-LIKE filter + sourceKind filter.
 *
 * Not a semantic search — for that, use the assistant-chat endpoint
 * which embeds the query and runs pgvector cosine distance.
 */

export const KnowledgeSourceKind = z.enum(['task', 'comment', 'file', 'page', 'chat_message']);
export type KnowledgeSourceKindValue = z.infer<typeof KnowledgeSourceKind>;

export const KnowledgeBrowseQuerySchema = z.object({
  /** Single-token substring match over chunk content.  Case-insensitive. */
  query: z.string().max(200).optional(),
  /** Restrict to specific source kinds.  Empty = all kinds. */
  sourceKinds: z.array(KnowledgeSourceKind).optional(),
  limit: z.number().int().min(1).max(100).default(20),
  offset: z.number().int().min(0).default(0),
});
export type KnowledgeBrowseQuery = z.infer<typeof KnowledgeBrowseQuerySchema>;

export const KnowledgeBrowseItemSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  sourceKind: KnowledgeSourceKind,
  sourceId: z.string(),
  sourceUri: z.string(),
  chunkIndex: z.number().int().nonnegative(),
  /** First 200 chars of content with ellipsis on truncation. */
  contentPreview: z.string(),
  /** True when the source was edited and the embedding hasn't been
   *  refreshed yet — chunks the AI shouldn't be quoting in answers. */
  isStale: z.boolean(),
  updatedAt: z.string().datetime(),
});
export type KnowledgeBrowseItem = z.infer<typeof KnowledgeBrowseItemSchema>;

export const KnowledgeBrowseResponseSchema = z.object({
  items: z.array(KnowledgeBrowseItemSchema),
  total: z.number().int().nonnegative(),
});
export type KnowledgeBrowseResponse = z.infer<typeof KnowledgeBrowseResponseSchema>;

/** POST /api/knowledge/citations/resolve — authenticated current-ACL navigation. */
export const KnowledgeCitationResolveInputSchema = z
  .object({ sourceUri: z.string().min(1).max(2048) })
  .strict();
export type KnowledgeCitationResolveInput = z.infer<typeof KnowledgeCitationResolveInputSchema>;

export const KnowledgeCitationResolveResponseSchema = z.object({
  /** Same-origin app route only; never an absolute or custom-protocol URL. */
  href: z.string().regex(/^\/(?!\/)/),
});
export type KnowledgeCitationResolveResponse = z.infer<
  typeof KnowledgeCitationResolveResponseSchema
>;
